import type { ReactElement } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { Auth } from './routes/Auth';
import { Onboarding } from './routes/Onboarding';
import { Welcome } from './routes/Welcome';
import { Dashboard } from './routes/Dashboard';
import { Reviews } from './routes/Reviews';
import { Requests } from './routes/Requests';
import { Customers } from './routes/Customers';
import { TextOptIns } from './routes/TextOptIns';
import { Templates } from './routes/Templates';
import { Automations } from './routes/Automations';
import ReplyLibrary from './routes/ReplyLibrary';
import { FeedbackGate } from './routes/FeedbackGate';
import { Feedback } from './routes/Feedback';
import { Widget } from './routes/Widget';
/* routes/Collection.tsx is deliberately NOT imported -- paused 2026-08-07
   (Steve) - QR Collection, may return. The screen has no QR generator yet, so
   it is withdrawn from the app; the file, its API client, and styles stay in
   the repo untouched so it can be restored. See the /collection redirect below.
   Leaving the import would only pull an unreachable screen into the bundle. */
/* routes/Social.tsx is deliberately NOT imported (Steve dropped social posting,
   2026-08-06 -- see the /social redirect below). The file and its components
   stay in the repo for the lane to resume; leaving the import would only pull a
   screen nobody can reach into the bundle. */
import { GbpPosts } from './routes/GbpPosts';
import { Competitors } from './routes/Competitors';
import { Listings } from './routes/Listings';
import { Reports } from './routes/Reports';
import { Settings } from './routes/Settings';
import { Tutorials } from './routes/Tutorials';
import { MoreMenu } from './routes/MoreMenu';
import { Integrations } from './routes/Integrations';
import { Agency } from './routes/Agency';
import { AdminTenantView } from './routes/AdminTenantView';
import { ReviewLander } from './routes/ReviewLander';
import { SetPassword } from './routes/SetPassword';
import { PostAuthLanding } from './routes/PostAuthLanding';
import { ChoosePlan } from './routes/ChoosePlan';
import { useSession } from './auth/useSession';
import { useSessionToken, useTokenSync, useSubscriptionAccess, useEntryModel } from './hooks';
import { trackAppBoot } from './lib/analytics';
import { capturePosthogPageview } from './lib/posthog';
import { consumeChuteResume, stashChuteGoogleReturn } from './lib/chuteResume';
import { useEffect, useRef, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

/* DEV-ONLY inline card-at-send preview (card-at-send inline, 2026-07-08). The
   import.meta.env.DEV constant is statically `false` in a production build, so
   this branch (and the dynamic import) are dead-code-eliminated -- the preview
   component is NEVER bundled or routable in prod. THROWAWAY: remove this + its
   route + routes/DevInlineCardPreview.tsx once the look is signed off. */
const DevInlineCardPreview = import.meta.env.DEV
  ? lazy(() => import('./routes/DevInlineCardPreview'))
  : null;

/* DEV-ONLY onboarding visual-polish previews (2026-07-08, Brad). Same dead-code
   pattern: statically dropped from prod builds. THROWAWAY -- remove with the
   /dev/* routes once the onboarding look is signed off. */
const DevConfirmBusinessPreview = import.meta.env.DEV
  ? lazy(() => import('./routes/DevConfirmBusinessPreview'))
  : null;
const DevDashboardLivePreview = import.meta.env.DEV
  ? lazy(() => import('./routes/DevDashboardLivePreview'))
  : null;

/* Multi-location management screen (FOUNDATION, 2026-08-18). Gated behind
   VITE_LOCATIONS_UI (default OFF) so it is not reachable on the live customer
   path until per-location provisioning is built and Steve signs off the scope.
   Same dead-code pattern as the dev previews: when the env is unset the flag is
   statically false, so the route + its lazy import are dropped from the prod
   bundle entirely. The backend /api/v1/locations routes are independently gated
   by LOCATIONS_API_ENABLED. */
const LOCATIONS_UI = import.meta.env.VITE_LOCATIONS_UI === '1';
const Locations = LOCATIONS_UI ? lazy(() => import('./routes/Locations')) : null;

/* Gate the app shell behind an authenticated Supabase session.
   Unauthenticated users are sent to /auth. (Post-login redirect to
   /onboarding vs /dashboard is handled inside Auth.tsx.)

   DEV-ONLY bypass: in the local dev preview (import.meta.env.DEV) we let
   you view the app without a session so screens are reviewable during the
   build. Production builds (DEV === false) ALWAYS enforce login. */
const DEV_PREVIEW = import.meta.env.DEV;

function RequireAuth({ children }: { children: ReactElement }) {
  const { session, loading } = useSession();
  if (loading) return null;
  if (!session && !DEV_PREVIEW) return <Navigate to="/auth" replace />;
  return children;
}

/* RequireSubscription -- the front-door subscription gate. Layered INSIDE
   RequireAuth on the AppShell route group.

   ENTRY-MODEL AWARE (Phase 5, spec §7 -- Steve's LOCKED decision: DO NOT deploy
   the front-door card wall). The hard-lock behavior now applies ONLY in the
   'front_door_wall' A/B arm; the DEFAULT 'reverse_trial' arm lets owners in
   card-free so they can reach the activation event, and the card ask is
   repositioned post-activation (PostActivationCardAsk in the shell).

     reverse_trial (DEFAULT) -> render the app (NO front-door wall). A
                                none/expired account is NOT bounced here; the
                                post-activation ask handles the card.
     front_door_wall (A/B)   -> the original 2026-07-06 hard lock:
       loading -> null (hold; never flash the gate at an operator or a still-
                  loading plan)
       allowed -> render the app  (whitelisted operator/demo, OR trial/active)
       gated   -> <Navigate to="/choose-plan" replace/>  (none/expired/canceled)

   The operator/demo whitelist + fail-open-on-error live in
   useSubscriptionAccess() and are preserved in the wall arm (allowed). In the
   reverse arm nobody is gated, so those protections are moot but intact.

   DEV bypass: in the local dev preview (import.meta.env.DEV) we let the app
   render un-gated so screens stay reviewable during the build, exactly like
   RequireAuth. */
function RequireSubscription({ children }: { children: ReactElement }) {
  const entryModel = useEntryModel();
  const { state } = useSubscriptionAccess();
  if (DEV_PREVIEW) return children;
  // Reverse-trial (default): no wall. Owners onboard card-free.
  if (entryModel === 'reverse_trial') return children;
  // Front-door-wall arm (A/B only; off by default): the 2026-07-06 hard lock.
  if (state === 'loading') return null;
  if (state === 'gated') return <Navigate to="/choose-plan" replace />;
  return children;
}

/* CheckoutReturnSync -- task #4 of the gate. When Stripe redirects the newly-
   subscribed user back into the app (backend sets success_url to
   /settings?billing=success), the cached ['billing-plan'] may still say
   has_active_subscription=false, which would bounce them to /choose-plan. On
   any ?billing=success landing we invalidate ['billing-plan'] (and ['admin',
   'me']) once so the gate re-reads fresh status and the user flows in without a
   manual reload. Mounted app-wide so it fires regardless of which route the
   redirect lands on. */
function CheckoutReturnSync() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const billing = new URLSearchParams(window.location.search).get('billing');
    if (billing === 'success' || billing === 'portal_return') {
      void queryClient.invalidateQueries({ queryKey: ['billing-plan'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'me'] });
    }
    // Run once on mount; the param is consumed by the Settings billing banner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/* ChuteResumeSync -- keeps the linear onboarding chute unbroken across the
   Google OAuth round-trip. The chute's connect step sets a sessionStorage flag
   and leaves for Google; the backend oauth_flow returns the owner to
   /integrations (a fixed redirect). On that landing we consume the flag and
   bounce them back to /welcome, which re-derives nextStep and lands them on the
   right step (test, once Google connected; connect if the listing still needs
   confirming). Mounted app-wide so it fires wherever the OAuth redirect lands. */
function ChuteResumeSync() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // Only act on the OAuth landing surface; never hijack a normal /welcome nav.
    if (pathname.startsWith('/welcome')) return;
    if (consumeChuteResume()) {
      // Carry the OAuth return status (e.g. scope_insufficient) into the chute.
      // The internal /welcome -> /welcome/connect redirect drops query params, so
      // stash it rather than relying on the URL surviving the bounce.
      try {
        stashChuteGoogleReturn(new URLSearchParams(window.location.search).get('google'));
      } catch {
        /* non-fatal */
      }
      navigate('/welcome', { replace: true });
    }
  }, [pathname, navigate]);
  return null;
}

/* App-wide token bridge. Mounting these ONCE here (not per-screen) fixes the
   cold-reload race for every token-gated screen, not just the Dashboard:
     - useSessionToken() bridges the Supabase login -> a fresh rr_ data token
       as soon as the session resolves, regardless of which route loaded first.
     - useTokenSync() invalidates queries the instant a new rr_ token lands, so
       a panel that fired with a stale/missing token refetches and populates
       without a navigate-away. */
function TokenBridge() {
  useSessionToken();
  useTokenSync();
  return null;
}

/* AnalyticsBoot -- fire the app_boot funnel event ONCE per page load (spec §11)
   and stamp first-seen for time-to-value. No PII; no network (stub bus). */
function AnalyticsBoot() {
  useEffect(() => {
    trackAppBoot();
  }, []);
  return null;
}

/* PosthogPageviews -- fire a PostHog $pageview on every client-side route
   change. The initial pageview is captured inside initPosthog() (main.tsx), so
   we skip the first effect run here to avoid double-counting. No-op entirely
   when PostHog is not keyed (capturePosthogPageview is inert until init). */
function PosthogPageviews() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    capturePosthogPageview(pathname);
  }, [pathname]);
  return null;
}

export default function App() {
  const { passwordRecovery, loading } = useSession();

  /* PASSWORD-RECOVERY GATE -- load-bearing security trap.
     When the user arrived via a password-recovery link, force the
     set-a-new-password screen BEFORE any routing into the app. The recovery
     link establishes a session (so RequireAuth would otherwise wave them in)
     but NO durable password is set yet. We render SetPassword over everything
     until the password is set (which clears passwordRecovery). We wait for the
     session to finish loading so a normal reload that still has the recovery
     hash in flight resolves the flag first. */
  if (!loading && passwordRecovery) {
    return (
      <>
        <TokenBridge />
        <SetPassword />
      </>
    );
  }

  return (
    <>
      <TokenBridge />
      <CheckoutReturnSync />
      <ChuteResumeSync />
      <AnalyticsBoot />
      <PosthogPageviews />
    <Routes>
      {/* Full-screen routes (outside the app shell) */}
      <Route path="/auth" element={<Auth />} />
      {/* DEV-ONLY: inline card-at-send visual preview (never in prod builds). */}
      {DevInlineCardPreview && (
        <Route
          path="/dev/inline-card"
          element={<Suspense fallback={null}><DevInlineCardPreview /></Suspense>}
        />
      )}
      {/* DEV-ONLY onboarding polish previews (never in prod builds). */}
      {DevConfirmBusinessPreview && (
        <Route
          path="/dev/confirm-business"
          element={<Suspense fallback={null}><DevConfirmBusinessPreview /></Suspense>}
        />
      )}
      {DevDashboardLivePreview && (
        <Route
          path="/dev/dashboard-live"
          element={<Suspense fallback={null}><DevDashboardLivePreview /></Suspense>}
        />
      )}
      {/* /start = marketing "Start free" CTA destination; opens the signup form directly. */}
      <Route path="/start" element={<Auth initialMode="signup" />} />
      {/* /operator = SEPARATE ADMIN DOOR (2026-06-22). Its own login UI; a
          successful login lands on /agency (the operator console) instead of
          the client dashboard. This is only a different DOOR/destination --
          admin authorization is still enforced SERVER-SIDE by @require_admin
          (admin_users gate). A non-admin who logs in here just hits the
          /agency "Not available" gate and sees no admin data. The MAIN login
          (/) deliberately does NOT route here, so Steve can sign in as a
          client (e.g. demo@) at the main door. */}
      <Route path="/operator" element={<Auth loginLandingPath="/agency" adminDoor />} />
      {/* /onboarding = the first-run wizard. Auth-gated (M6): a logged-out
          visitor must not load the wizard shell (its data calls would 401
          anyway, but we fail closed at the route). Full-screen, outside the
          app shell. */}
      <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
      {/* /welcome = the LINEAR onboarding CHUTE (2026-07-09). Full-screen,
          outside the app shell. Auth-gated but deliberately NOT subscription-
          gated: the chute is card-free, so a card-free reverse-trial owner must
          reach it. Each step is its own deep-linkable sub-route; /welcome (no
          step) redirects to the derived nextStep. */}
      <Route path="/welcome" element={<RequireAuth><Welcome /></RequireAuth>} />
      <Route path="/welcome/:step" element={<RequireAuth><Welcome /></RequireAuth>} />
      {/* /choose-plan = the CARD-REQUIRED front door + hard-lock plan picker
          (2026-07-06 gate). Auth-gated (RequireAuth) but deliberately NOT
          subscription-gated -- it IS the screen a no-active-subscription tenant
          is allowed to reach. Full-screen, OUTSIDE the AppShell. */}
      <Route path="/choose-plan" element={<RequireAuth><ChoosePlan /></RequireAuth>} />
      {/* /r/<embed_key> = PUBLIC leave-a-review lander (audit C3). This is
          where every printed QR / NFC card / email-signature link points; a
          business's CUSTOMER lands here, so it must never require login.
          Deliberately OUTSIDE RequireAuth + the app shell. */}
      <Route path="/r/:key" element={<ReviewLander />} />
      {/* /agency = standalone full-bleed operator console (its own chrome,
          deliberately OUTSIDE AppShell). Gated by RequireAuth so the screen is
          not publicly reachable in production (DEV preview still bypasses).
          Data is LIVE from the /api/v1/admin/* endpoints (rollups, tenants,
          queue); admin authorization is enforced server-side by @require_admin. */}
      <Route path="/agency" element={<RequireAuth><Agency /></RequireAuth>} />
      {/* /agency/tenant/:id = OPTION A operator read-only view of one client's
          FULL dashboard (reuses the real Dashboard + Reviews components in
          read-only mode). Admin-gated server-side by @require_admin; the screen
          also fails closed for non-admins. Outside AppShell, like /agency. */}
      <Route
        path="/agency/tenant/:id"
        element={<RequireAuth><AdminTenantView /></RequireAuth>}
      />

      {/* App shell routes (auth-gated + SUBSCRIPTION-gated). RequireSubscription
          is layered INSIDE RequireAuth: a valid session is necessary but no
          longer sufficient -- a none/expired/canceled account is hard-locked to
          /choose-plan (2026-07-06 monetization gate). Operator/demo + DEV
          bypass pass through. */}
      <Route element={<RequireAuth><RequireSubscription><AppShell /></RequireSubscription></RequireAuth>}>
        {/* Index = role-aware landing: operators -> /agency, clients -> /dashboard. */}
        <Route index element={<PostAuthLanding />} />
        {/* /first-run RETIRED (Phase 3): the unified checklist home lives at
            /onboarding (and overlays a returning zero-data dashboard). Any old
            link redirects there so nothing 404s. */}
        <Route path="/first-run" element={<Navigate to="/onboarding" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/customers" element={<Customers />} />
        {/* Text opt-ins (Penn Condition 1, 2026-07-29): per-customer
            texting permission -- who asked, who confirmed, who cannot be
            texted and why. Separate screen so the Customers list stays a
            contact list. */}
        <Route path="/text-opt-ins" element={<TextOptIns />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/automations" element={<Automations />} />
        <Route path="/replies" element={<ReplyLibrary />} />
        <Route path="/feedback-gate" element={<FeedbackGate />} />
        <Route path="/widget" element={<Widget />} />
        {/* COLLECTION (QR / short-links) PAUSED 2026-08-07 (Steve) -- may return.
            The screen shipped without a QR generator, so it is withdrawn from the
            app. Same treatment as /social above: the route is redirected instead
            of removed, so an old bookmark or deep link resolves to the Dashboard
            rather than a dead end, and the Collection component/API client/styles
            stay in the repo untouched. Restore the import + this line as
            <Route path="/collection" element={<Collection />} /> to bring it back. */}
        <Route path="/collection" element={<Navigate to="/dashboard" replace />} />
        {/* SOCIAL POSTING IS DROPPED FROM THE PRODUCT (Steve, 2026-08-06):
            "I'm pretty sure I'm going to forgo the social posting at this time
            and just use the Google Business Profile posting."

            The nav entry was commented out on 2026-07-06, but the ROUTE stayed
            mounted, so typing /social still landed on a working Facebook and
            Instagram screen. That is now a straight contradiction of the
            marketing site, which has been swept clean of every social-posting
            claim, and there are no Meta credentials in prod anyway. A trial
            user finding that screen concludes the product is broken.

            Redirected to /google-posts, which mirrors the 301 the marketing
            site serves for /features/social-posting and points at the
            capability that actually replaced it. NOT a "coming soon" -- that
            would re-create the same overclaim by another route.

            The Social screen and its components stay in the repo untouched so
            the lane can resume; this is about reachability, not deletion. */}
        <Route path="/social" element={<Navigate to="/google-posts" replace />} />
        <Route path="/google-posts" element={<GbpPosts />} />
        <Route path="/competitors" element={<Competitors />} />
        {Locations && (
          <Route
            path="/locations"
            element={<Suspense fallback={null}><Locations /></Suspense>}
          />
        )}
        <Route path="/listings" element={<Listings />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/tutorials" element={<Tutorials />} />
        {/* Mobile-only "More" menu (secondary nav). On desktop the sidebar
            exposes these, so this route is only reached from the bottom-tab
            "More" item, which is hidden above 840px. */}
        <Route path="/more" element={<MoreMenu />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </>
  );
}
